import { CommandId } from "@afar-research/contracts";
import type {
  DesignCollectionId,
  DesignDeleteReport,
  DesignDeleteTarget,
  DesignDeleteTargetOutcome,
  DesignSystemFailure,
} from "@afar-research/contracts";

/**
 * The Delete Design Operation's selection and reporting rules, in one place
 * because three surfaces read them: the picker (what a row may be checked to),
 * the composer footer (what the send button promises), and the conversation
 * (what the one host-authored entry says happened).
 *
 * Nothing here touches the filesystem. `packages/afar/src/designSystems.ts`
 * owns what a delete *does*; this owns what a delete is *called*.
 */

/**
 * The product noun a Design Artifact Group's collection is named by. The disk
 * says `outputs/` for Samples and the schema says `samples`; neither is what
 * the user chose in the composer, so neither is what a picker row says.
 */
export function designCollectionProductNoun(collection: DesignCollectionId): string {
  switch (collection) {
    case "templates":
      return "Template";
    case "examples":
      return "Example";
    case "samples":
      return "Sample";
  }
}

/**
 * Stable within one selection, and stable across a re-listing: the picker keys
 * its rows by this, and the report keys its items by it.
 */
export function designDeleteTargetKey(target: DesignDeleteTarget): string {
  switch (target.kind) {
    case "system":
      return `system:${target.designSystemSlug}`;
    case "artifact-group":
      return `group:${target.designSystemSlug}:${target.collection}:${target.groupSlug}`;
    case "invalid-system":
      return `invalid:${target.relativePath}`;
  }
}

/**
 * Whether a whole Design System in the selection already covers this target.
 *
 * Checking a system checks and disables its own group rows, so a redundant
 * selection cannot be constructed in the first place. This is the rule behind
 * that: collapsing redundant targets silently at submission time would make the
 * count the user reads disagree with what is sent, and rejecting the selection
 * as a conflict would punish the user for something the picker allowed.
 */
export function isDesignDeleteTargetCovered(
  target: DesignDeleteTarget,
  selection: ReadonlyArray<DesignDeleteTarget>,
): boolean {
  if (target.kind !== "artifact-group") return false;
  return selection.some(
    (selected) =>
      selected.kind === "system" && selected.designSystemSlug === target.designSystemSlug,
  );
}

/**
 * The selection after checking or unchecking one row. Checking a system drops
 * the group rows it now covers, so the selection never holds a target another
 * target already contains.
 */
export function toggleDesignDeleteTarget(
  selection: ReadonlyArray<DesignDeleteTarget>,
  target: DesignDeleteTarget,
): ReadonlyArray<DesignDeleteTarget> {
  const key = designDeleteTargetKey(target);
  if (selection.some((selected) => designDeleteTargetKey(selected) === key)) {
    return selection.filter((selected) => designDeleteTargetKey(selected) !== key);
  }
  const next = [...selection, target];
  return next.filter((selected) => !isDesignDeleteTargetCovered(selected, next));
}

/** How a target names itself in a chip, a picker row, and a report item. */
export function designDeleteTargetLabel(target: DesignDeleteTarget): string {
  switch (target.kind) {
    case "system":
      return `${target.name} · ${target.designSystemSlug}`;
    case "artifact-group":
      return `${designCollectionProductNoun(target.collection)} · ${target.groupSlug}`;
    case "invalid-system":
      return target.relativePath;
  }
}

interface DesignDeleteSelectionCounts {
  readonly systems: number;
  readonly groups: number;
}

function countDesignDeleteSelection(
  targets: ReadonlyArray<DesignDeleteTarget>,
): DesignDeleteSelectionCounts {
  let systems = 0;
  let groups = 0;
  for (const target of targets) {
    // An invalid system is still a Design System being removed whole, and a
    // separate noun for it would only name the listing's diagnosis, not the act.
    if (target.kind === "artifact-group") groups += 1;
    else systems += 1;
  }
  return { systems, groups };
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function joinDesignDeleteCounts(counts: DesignDeleteSelectionCounts): string {
  const parts = [
    ...(counts.systems > 0 ? [pluralize(counts.systems, "system")] : []),
    ...(counts.groups > 0 ? [pluralize(counts.groups, "group")] : []),
  ];
  return parts.join(" and ");
}

/**
 * What the send button promises. Send *is* the confirmation for this operation
 * — there is no typed name or slug — so the button names the act and its size
 * rather than saying "Send".
 */
export function designDeleteSendLabel(targets: ReadonlyArray<DesignDeleteTarget>): string {
  if (targets.length === 0) return "Delete";
  return `Delete ${joinDesignDeleteCounts(countDesignDeleteSelection(targets))}`;
}

/** Why a target was left alone, said to the user rather than to the log. */
export function designDeleteRefusalText(reason: DesignSystemFailure | undefined): string {
  switch (reason) {
    case "revision-conflict":
      return "changed before delete";
    case "confirmation-mismatch":
      return "renamed before delete";
    case "system-not-found":
      return "no such Design System";
    case "group-not-found":
      return "no such artifact group";
    case "system-not-invalid":
      return "no longer invalid";
    case "unsafe-path":
      return "unsafe path";
    case "project-busy":
      return "the project was busy";
    case "design-workspace-unavailable":
      return "the Design Workspace was unavailable";
    case undefined:
      return "refused";
    default:
      return reason;
  }
}

export interface DesignDeleteReportItem {
  readonly id: string;
  readonly text: string;
  readonly status: "deleted" | "refused";
}

export interface DesignDeleteReportSummary {
  /** Names the act and its result in one line, counts included. */
  readonly headline: string;
  readonly items: ReadonlyArray<DesignDeleteReportItem>;
  /** True when at least one target was refused, which tints the entry. */
  readonly hasRefusals: boolean;
}

/**
 * The one host-authored timeline entry, derived from the report rather than
 * pre-formatted into it: the conversation draws the whole thing while a denser
 * surface may want only the headline, and neither should re-parse a string.
 *
 * A mixed fan-out is still one entry, because the user performed one act.
 */
export function summarizeDesignDeleteReport(report: DesignDeleteReport): DesignDeleteReportSummary {
  const deleted = report.outcomes.filter((outcome) => outcome.status === "deleted");
  const total = report.outcomes.length;
  const headline =
    deleted.length === total
      ? `Deleted ${joinDesignDeleteCounts(countDesignDeleteSelection(deleted.map((outcome) => outcome.target)))}`
      : `Deleted ${deleted.length} of ${total}`;
  return {
    headline,
    hasRefusals: deleted.length !== total,
    items: report.outcomes.map((outcome) => ({
      id: designDeleteTargetKey(outcome.target),
      text:
        outcome.status === "deleted"
          ? designDeleteTargetLabel(outcome.target)
          : `${designDeleteTargetLabel(outcome.target)} — ${designDeleteRefusalText(outcome.reason)}`,
      status: outcome.status,
    })),
  };
}

/** The Thread Activity kind the fan-out appends. One per send, never per target. */
export const DESIGN_DELETE_REPORTED_ACTIVITY_KIND = "design.delete.reported";

/**
 * The report carried by one `design.delete.reported` Thread Activity, or null
 * for any other activity and for a payload this build does not recognise.
 *
 * Narrow rather than schema-decoded: the activity payload is `unknown` by
 * design, a client reads activities written by every version of the service
 * that has ever run against this store, and an unreadable report should fall
 * out of the timeline rather than fail the thread around it.
 */
export function readDesignDeleteReport(activity: {
  readonly kind: string;
  readonly payload: unknown;
}): DesignDeleteReport | null {
  if (activity.kind !== DESIGN_DELETE_REPORTED_ACTIVITY_KIND) return null;
  const payload = activity.payload;
  if (typeof payload !== "object" || payload === null) return null;
  if (!("commandId" in payload) || typeof payload.commandId !== "string") return null;
  if (!("outcomes" in payload) || !Array.isArray(payload.outcomes)) return null;
  const outcomes = payload.outcomes.filter(
    (outcome): outcome is DesignDeleteTargetOutcome =>
      typeof outcome === "object" &&
      outcome !== null &&
      "target" in outcome &&
      typeof outcome.target === "object" &&
      outcome.target !== null &&
      "kind" in outcome.target &&
      "status" in outcome &&
      (outcome.status === "deleted" || outcome.status === "refused"),
  );
  return outcomes.length === 0 ? null : { commandId: CommandId.make(payload.commandId), outcomes };
}
